import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tooltip from '@mui/material/Tooltip';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningIcon from '@mui/icons-material/Warning';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ChecklistIcon from '@mui/icons-material/Checklist';

import DetailsView from '../Common/Views/DetailsView';
import ImageThumbnailGrid from '../Common/ImageThumbnailGrid';
import ImageGalleryModal from '../Common/ImageGalleryModal';
import ImagePreviewModal from '../Common/ImagePreviewModal';
import { normalizeApiImage, getImageUrl } from './imageUtils';
import { formatPriceWithCommas } from '../utils/stringUtils';
import JobCardChecklistView from './ChecklistView';
import HumanTime from '../core/formatters/HumanTime';

// Non-invoicable reason labels
const NON_INVOICABLE_REASON_LABELS = {
    'WARRANTY': 'Warranty Work',
    'INTERNAL': 'Internal / Company Vehicle',
    'GOODWILL': 'Goodwill / Complimentary',
    'OTHER': 'Other',
};

// Status configuration
const STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: 'default' },
    ASSIGNED: { label: 'Assigned', color: 'info' },
    IN_PROGRESS: { label: 'In Progress', color: 'warning' },
    COMPLETED: { label: 'Completed', color: 'success' },
    CANCELLED: { label: 'Cancelled', color: 'error' },
};

const PRIORITY_LABELS = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    URGENT: 'Urgent',
};

// --- Checklist summary stats helper ---
function computeChecklistStats(checklist) {
    if (!checklist) return { ok: 0, notOk: 0, total: 0 };
    const { templateSnapshot, responses, stage } = checklist;
    let ok = 0, notOk = 0, total = 0;
    if (templateSnapshot?.sections && responses) {
        for (const section of templateSnapshot.sections) {
            if (!section.stages.includes(stage)) continue;
            for (const item of section.items) {
                total++;
                const r = responses[section.key]?.[item.key];
                if (r?.ok === true) ok++;
                else if (r?.ok === false) notOk++;
            }
        }
    }
    return { ok, notOk, total };
}

// --- Data layout rendered inside DetailsView ---
function JobCardDataLayout({ data }) {
    const statusConfig = STATUS_CONFIG[data.status] || STATUS_CONFIG.DRAFT;

    return (
        <>
            {/* Status & Priority chips */}
            <Stack direction="row" spacing={1} mb={1}>
                <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
                <Chip label={PRIORITY_LABELS[data.priority] || data.priority} variant="outlined" size="small" />
            </Stack>

            <Divider />

            {/* Vehicle, Location, Employee */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                    <Typography variant="body2">
                        {data.vehicle?.registrationNumber || '-'}
                    </Typography>
                    {data.vehicle && (
                        <Typography variant="caption" color="text.secondary">
                            {[data.vehicle.make, data.vehicle.model, data.vehicle.year].filter(Boolean).join(' • ')}
                            {data.vehicle.color ? ` • ${data.vehicle.color}` : ''}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                    <Typography variant="body2">{data.location?.name || '-'}</Typography>
                    {data.location && (
                        <Typography variant="caption" color="text.secondary">
                            {[data.location.type, data.location.address].filter(Boolean).join(' • ')}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                    <Typography variant="body2">{data.assignedEmployee?.name || 'Not assigned'}</Typography>
                    {data.assignedEmployee?.phoneNumber && (
                        <Typography variant="caption" color="text.secondary">
                            {data.assignedEmployee.phoneNumber}
                        </Typography>
                    )}
                </Grid>
            </Grid>

            <Divider />

            {/* Mileage & Notes */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Current Mileage</Typography>
                    <Typography variant="body2">
                        {data.currentMileage ? `${data.currentMileage.toLocaleString()} km` : '-'}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Next Service Mileage</Typography>
                    <Typography variant="body2">
                        {data.nextServiceMileage ? `${data.nextServiceMileage.toLocaleString()} km` : '-'}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Customer Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {data.customerNotes || '-'}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Internal Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: data.internalNotes ? 'normal' : 'italic' }}>
                        {data.internalNotes || '-'}
                    </Typography>
                </Grid>
            </Grid>

            <Divider />

            {/* Timeline */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>Timeline</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={4} lg={3}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2"><HumanTime isoDate={data.createdAt} includeTime /></Typography>
                </Grid>
                {data.assignedAt && (
                    <Grid item xs={6} sm={4} lg={3}>
                        <Typography variant="caption" color="text.secondary">Assigned</Typography>
                        <Typography variant="body2"><HumanTime isoDate={data.assignedAt} includeTime /></Typography>
                    </Grid>
                )}
                {data.startedAt && (
                    <Grid item xs={6} sm={4} lg={3}>
                        <Typography variant="caption" color="text.secondary">Started</Typography>
                        <Typography variant="body2"><HumanTime isoDate={data.startedAt} includeTime /></Typography>
                    </Grid>
                )}
                {data.completedAt && (
                    <Grid item xs={6} sm={4} lg={3}>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                        <Typography variant="body2"><HumanTime isoDate={data.completedAt} includeTime /></Typography>
                    </Grid>
                )}
                {data.canceledAt && (
                    <Grid item xs={6} sm={4} lg={3}>
                        <Typography variant="caption" color="text.secondary">Cancelled</Typography>
                        <Typography variant="body2"><HumanTime isoDate={data.canceledAt} includeTime /></Typography>
                        {data.cancelReason && (
                            <Typography variant="caption" color="error">Reason: {data.cancelReason}</Typography>
                        )}
                    </Grid>
                )}
            </Grid>

            <Divider />

            {/* Billing */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Billing</Typography>
                    {data.isInvoiceable ? (
                        <Chip label="Billable to Customer" color="success" size="small" sx={{ mt: 0.5 }} />
                    ) : (
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                                <WarningIcon color="warning" fontSize="small" />
                                <Chip label="Non-Billable" color="warning" size="small" variant="outlined" />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {NON_INVOICABLE_REASON_LABELS[data.nonInvoicableReason] || data.nonInvoicableReason}
                            </Typography>
                            {data.nonInvoicableNote && (
                                <Typography variant="body2" color="text.secondary">
                                    Note: {data.nonInvoicableNote}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Grid>
            </Grid>
        </>
    );
}

// --- Main component ---
const JobCardDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    // Job card data (synced from DetailsView render prop)
    const [jobCardData, setJobCardData] = useState(null);

    // Work order state
    const [workOrder, setWorkOrder] = useState(null);
    const [workOrderItems, setWorkOrderItems] = useState([]);
    const [woLoading, setWoLoading] = useState(false);

    // Image modal states
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);

    const images = useMemo(() => {
        if (!jobCardData?.images) return [];
        return jobCardData.images.map(img => normalizeApiImage(img));
    }, [jobCardData?.images]);

    // Load work order when job card data becomes available
    useEffect(() => {
        const loadWorkOrder = async () => {
            if (!jobCardData) return;
            if (!['IN_PROGRESS', 'COMPLETED'].includes(jobCardData.status)) {
                setWorkOrder(null);
                setWorkOrderItems([]);
                return;
            }
            try {
                setWoLoading(true);
                const workOrdersRes = await tenantApi(`/tenants/work-orders`, {
                    query: { originId: id, originType: 'JOB' }
                });
                if (workOrdersRes.data?.length > 0) {
                    const wo = workOrdersRes.data[0];
                    setWorkOrder(wo);
                    const itemsRes = await tenantApi(`/tenants/work-orders/${wo.id}/items`);
                    setWorkOrderItems(itemsRes.data || []);
                }
            } catch (err) {
                console.error('Error loading work order:', err);
            } finally {
                setWoLoading(false);
            }
        };
        loadWorkOrder();
    }, [jobCardData?.status, id]);

    // Image handlers
    const handleImageClick = useCallback((index) => {
        setPreviewIndex(index);
        setPreviewOpen(true);
    }, []);

    const handleRequestUrl = useCallback(async (imageId) => {
        return await getImageUrl(tenantApi, id, imageId);
    }, [tenantApi, id]);

    // Checkout logic
    const isCompleted = jobCardData?.status === 'COMPLETED';
    const isCancelled = jobCardData?.status === 'CANCELLED';
    const hasWorkOrder = !!workOrder;
    const isInvoiced = workOrder?.status === 'INVOICED';
    const canCheckout = isCompleted && hasWorkOrder && !isInvoiced;

    const getCheckoutMessage = () => {
        if (isCancelled) return 'Cancelled job cards cannot be checked out.';
        if (isInvoiced) return 'This job card has already been invoiced.';
        if (!isCompleted) return 'Job card must be completed before checkout.';
        if (!hasWorkOrder) return 'No work order found for this job card.';
        return null;
    };

    // Checklist stats for accordion summary
    const startChecklist = useMemo(() => (jobCardData?.checklists || []).find(c => c.stage === 'START'), [jobCardData?.checklists]);
    const completeChecklist = useMemo(() => (jobCardData?.checklists || []).find(c => c.stage === 'COMPLETE'), [jobCardData?.checklists]);
    const startStats = useMemo(() => computeChecklistStats(startChecklist), [startChecklist]);
    const completeStats = useMemo(() => computeChecklistStats(completeChecklist), [completeChecklist]);
    const hasChecklists = jobCardData?.checklists?.length > 0;

    const checklistSummary = useMemo(() => {
        const parts = [];
        if (startChecklist) parts.push(`Reception: ${startStats.ok}/${startStats.total} OK`);
        if (completeChecklist) parts.push(`Completion: ${completeStats.ok}/${completeStats.total} OK`);
        return parts.join(' • ');
    }, [startChecklist, completeChecklist, startStats, completeStats]);

    return (
        <>
            {/* Basic details via DetailsView */}
            <DetailsView
                title="Job Card"
                subtitleAttribute="jobNumber"
                apiPath={`/tenants/job-cards/${id}`}
            >
                {({ data }) => {
                    // Sync data to outer state so work order fetch can trigger
                    if (data && data !== jobCardData) {
                        Promise.resolve().then(() => setJobCardData(data));
                    }
                    return <JobCardDataLayout data={data} />;
                }}
            </DetailsView>

            {/* Work Order Items + Checkout + Photos + Checklists */}
            <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: { xs: 1, sm: 2, md: 3 } }}>

                {/* Work Order Items Table */}
                {workOrder && workOrderItems.length > 0 && (
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <AssignmentIcon color="primary" />
                                <Typography variant="h6">Work Order Items</Typography>
                            </Stack>
                            <Chip
                                label={workOrder.status}
                                size="small"
                                color={['CLOSED', 'INVOICED'].includes(workOrder.status) ? 'success' : 'warning'}
                            />
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {workOrderItems.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{item.itemData?.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.itemData?.type === 'SERVICE' ? 'Service' : 'Product'}
                                                    size="small"
                                                    variant="outlined"
                                                    color={item.itemData?.type === 'SERVICE' ? 'info' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{item.quantity} {item.itemData?.uom}</TableCell>
                                            <TableCell align="right">
                                                {formatPriceWithCommas(item.unitPrice || item.itemData?.unitPrice || 0)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="bold">
                                                    {formatPriceWithCommas(
                                                        ((parseFloat(item.unitPrice || item.itemData?.unitPrice) || 0) *
                                                            (parseFloat(item.quantity) || 0)).toFixed(2)
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Total */}
                                    <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                        <TableCell colSpan={5} align="right">
                                            <Typography variant="subtitle1" fontWeight="bold">Total</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {formatPriceWithCommas(
                                                    workOrderItems
                                                        .reduce((sum, item) => {
                                                            const unitPrice = parseFloat(item.unitPrice || item.itemData?.unitPrice || 0);
                                                            const quantity = parseFloat(item.quantity || 0);
                                                            return sum + (unitPrice * quantity);
                                                        }, 0).toFixed(2)
                                                )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Checkout Button */}
                {jobCardData && !isCancelled && (
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                            {/* Invoice link if already invoiced */}
                            {isInvoiced && workOrder?.invoiceId && (
                                <Button
                                    component={Link}
                                    to={`/sales/invoices/${workOrder.invoiceId}`}
                                    startIcon={<ReceiptIcon />}
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                >
                                    View Invoice
                                </Button>
                            )}

                            {/* Checkout button */}
                            {!isInvoiced && (
                                <Tooltip title={!canCheckout ? getCheckoutMessage() : ''} arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<ShoppingCartCheckoutIcon />}
                                            disabled={!canCheckout}
                                            onClick={() => navigate(`/sales/pos/${workOrder.id}`)}
                                        >
                                            Checkout
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}

                            {/* Info message when checkout is not available */}
                            {!canCheckout && !isInvoiced && (
                                <Alert severity="info" sx={{ py: 0, flex: 1 }}>
                                    {getCheckoutMessage()}
                                </Alert>
                            )}
                        </Stack>
                    </Paper>
                )}

                {/* Photos */}
                {images.length > 0 && (
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={500} mb={2}>
                            Photos
                        </Typography>
                        <ImageThumbnailGrid
                            images={images}
                            onImageClick={handleImageClick}
                            onViewAllClick={() => setGalleryOpen(true)}
                            maxVisible={6}
                            showStageLabels={true}
                        />
                    </Paper>
                )}

                {/* Checklists - collapsed accordion */}
                {hasChecklists && (
                    <Accordion
                        elevation={1}
                        sx={{
                            '&:before': { display: 'none' },
                            borderRadius: '4px !important',
                            overflow: 'hidden',
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <ChecklistIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight={500}>
                                    Inspection Checklists
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    — {checklistSummary}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <JobCardChecklistView checklists={jobCardData.checklists} />
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>

            {/* Image Gallery Modal */}
            <ImageGalleryModal
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                images={images}
                showStageLabels={true}
                onRequestUrl={handleRequestUrl}
            />

            {/* Image Preview Modal */}
            <ImagePreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                images={images}
                initialIndex={previewIndex}
                onRequestUrl={handleRequestUrl}
            />
        </>
    );
};

export default JobCardDetails;
